/**
 * @file HowToUsePage.jsx
 * @description "How to use this application" — the per-department user guide.
 *
 * Everyone signed in can open it (like Profile/Calendar). A user sees the
 * General tab plus a tab for each department they belong to; the super admin
 * sees every department and can edit sections in place (add / edit / delete —
 * stored in app_guides via /api/app-info). Content is plain text; blank lines
 * separate paragraphs and "- " lines read as bullets.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Card, CardContent, Tabs, Tab, Stack, Skeleton, Alert,
    IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Chip,
} from '@mui/material';
import {
    Edit as EditIcon, Delete as DeleteIcon,
    AddCircleOutline as AddIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin, userDepartments } from '../../utils/permissions';
import appInfoService from '../../services/appInfoService';

const DEPT_LABELS = {
    general: 'General', service: 'Service', sales: 'Sales',
    operations: 'Operations', financial: 'Financial', marketing: 'Marketing',
};
const ALL_DEPTS = ['general', 'service', 'sales', 'operations', 'financial'];

const HowToUsePage = () => {
    const { user } = useAuth();
    const superAdmin = isSuperAdmin(user);

    // Tabs: General for everyone; each granted department; super admin sees all.
    const tabs = useMemo(() => {
        if (superAdmin) return ALL_DEPTS;
        const mine = userDepartments(user);
        return ['general', ...ALL_DEPTS.filter(d => d !== 'general' && mine.includes(d))];
    }, [user, superAdmin]);

    const [tab, setTab] = useState(tabs[0] || 'general');
    const [sections, setSections] = useState(null);   // all sections, all depts
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await appInfoService.listGuides();
            setSections(res?.sections || []);
            setError('');
        } catch (e) {
            setError(e?.message || 'Failed to load the guide');
            setSections([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const shown = useMemo(
        () => (sections || []).filter(s => s.department === tab),
        [sections, tab]);

    // ── Super-admin editing ──────────────────────────────────────────────────
    const [editor, setEditor] = useState(null); // {id?, title, body} — null closed
    const [busy, setBusy] = useState(false);

    const save = async () => {
        if (!editor?.title?.trim() || !editor?.body?.trim()) return;
        setBusy(true);
        try {
            if (editor.id) await appInfoService.updateGuide(editor.id, { title: editor.title, body: editor.body });
            else await appInfoService.createGuide({ department: tab, title: editor.title, body: editor.body });
            setEditor(null);
            await load();
        } catch (e) {
            setError(e?.message || 'Save failed');
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        setBusy(true);
        try { await appInfoService.deleteGuide(id); await load(); }
        catch (e) { setError(e?.message || 'Delete failed'); }
        finally { setBusy(false); }
    };

    return (
        <Box p={3} className="animate-fade-in">
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                variant="scrollable" scrollButtons="auto">
                {tabs.map(d => <Tab key={d} value={d} label={DEPT_LABELS[d] || d} sx={{ fontWeight: 700, textTransform: 'none' }} />)}
            </Tabs>

            {sections === null ? (
                <Stack spacing={2}>{[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={120} />)}</Stack>
            ) : (
                <Stack spacing={2} sx={{ maxWidth: 900 }}>
                    {shown.length === 0 && (
                        <Card variant="outlined" sx={{ borderRadius: '14px' }}>
                            <CardContent>
                                <Typography color="text.secondary">
                                    No guide has been written for {DEPT_LABELS[tab]} yet.
                                    {superAdmin ? ' Use "Add section" below to start it.' : ''}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                    {shown.map(s => (
                        <Card key={s.id} variant="outlined" sx={{ borderRadius: '14px' }}>
                            <CardContent sx={{ '&:last-child': { pb: 2.5 } }}>
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>{s.title}</Typography>
                                    {superAdmin && (
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Edit section">
                                                <IconButton size="small" onClick={() => setEditor({ id: s.id, title: s.title, body: s.body })}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete section">
                                                <IconButton size="small" color="error" disabled={busy} onClick={() => remove(s.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                </Stack>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'text.primary', lineHeight: 1.7 }}>
                                    {s.body}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                    {superAdmin && (
                        <Button startIcon={<AddIcon />} variant="outlined" sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
                            onClick={() => setEditor({ title: '', body: '' })}>
                            Add section to {DEPT_LABELS[tab]}
                        </Button>
                    )}
                </Stack>
            )}

            <Dialog open={!!editor} onClose={() => setEditor(null)} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {editor?.id ? 'Edit section' : `New section — ${DEPT_LABELS[tab]}`}
                    <Chip label={DEPT_LABELS[tab]} size="small" sx={{ ml: 1.5, fontWeight: 700 }} />
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField label="Title" value={editor?.title || ''} fullWidth size="small"
                            onChange={e => setEditor(p => ({ ...p, title: e.target.value }))} />
                        <TextField label="Content" value={editor?.body || ''} fullWidth multiline minRows={8}
                            helperText='Plain text. Blank line = new paragraph; start a line with "- " for a bullet.'
                            onChange={e => setEditor(p => ({ ...p, body: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditor(null)}>Cancel</Button>
                    <Button variant="contained" onClick={save} disabled={busy || !editor?.title?.trim() || !editor?.body?.trim()}>
                        {busy ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HowToUsePage;
